/* eslint-disable camelcase */
// @flow

import type { Store } from 'redux';
import {
  encrypt,
  mean,
  getVersionMajor,
  getVersionMinor,
  getVersionRevision,
} from 'Common/lib/player/managers/StatspostUtils';
import { Stopwatch } from 'Common/utilities/timeUtils';
import { isLiveLinearSourceType } from 'Common/models/franchise';
import type { State, BaseAction, ThunkAction, Dispatch } from 'Common/types';

import BaseCastAnalytics from './BaseCastAnalytics';

const TAG = 'STATSPOST[CAST]';

const STATS_POST_BETA_URL = 'http://b-stats.movetv.com/stats/Playback';
const STATS_POST_PROD_URL = 'http://p-stats.movetv.com/stats/Playback';
const MS_IN_SECOND = 1000;
const UPDATE_REPORT_INTERVAL = 15 * 60 * MS_IN_SECOND; // Reporting every 15 minutes due to 'Internal Analytics Integration Guide'
const BITRATE_UPDATE_INTERVAL = 10 * MS_IN_SECOND;

const PLAYER_STATE = {
  // The state of the player at the time this record was generated.
  STOPPED: 0,
  ERROR: 1,
  LOADING: 3,
  PLAYING: 7,
  PAUSED: 8,
};

class StatsPostCastAnalytics extends BaseCastAnalytics {
  _sequenceId: number;
  _sessionId: ?number;
  _playerState: number;
  _aggregateBitrates: Array<number>;
  _updateBitrateIntervalId: IntervalID;
  _updateReportIntervalId: IntervalID;
  _lastUpdateDate: Date;
  _startPlaybackTimeInMs: number;
  _viewedTime: ?Object;

  constructor(playerManager: Object, store: Store<State, BaseAction | ThunkAction, Dispatch>) {
    super(playerManager, store);
    this._playerState = PLAYER_STATE.STOPPED;
    this._startPlaybackTimeInMs = 0;
    this._aggregateBitrates = [];
    this._viewedTime = undefined;
    this.isEventBasedLlTracking = false;
    this.emptyValue = 'null';
  }

  get enabled() {
    return isLiveLinearSourceType(this.sourceType);
  }

  get entitlementCategory() {
    return 'Other'; // 0=Other, 1=VOD, 2=Subscription
  }

  get serviceId() {
    return `${this.publisherId}|${this.environment}|${this.state.config.appName}`; // publisherid|environment|name
  }

  get environment() {
    return this.state.config.base.env;
  }

  get postUrl() {
    return this.environment === 'production' ? STATS_POST_PROD_URL : STATS_POST_BETA_URL;
  }

  get contentSource() {
    return 0; // The ID for the content source. 0=CDN, 1=RSDVR, 2=LSDVR, 3=Aereo, 4=Disney
  }

  _initialize() {
    if (this.isInitialized) return;

    this._sequenceId = 0; // A unique ID that increments with each new report that's generated during the session. Starts at 0.
    this._sessionId = this.sessionId; // Random number generated for each new session.
    this._playerState = PLAYER_STATE.LOADING;
    this._startPlaybackTimeInMs = this.playbackTimeInMs;
    this._viewedTime = new Stopwatch();

    this._updateBitrateIntervalId = setInterval(
      // collect all bitrates during playback session
      () => this._aggregateBitrates.push(this.streamBandwidth),
      BITRATE_UPDATE_INTERVAL,
    );

    this._updateReportIntervalId = setInterval(
      // Reporting every 15 minutes due to 'Internal Analytics Integration Guide'
      () => this._postReport(this._createContentUpdatedReport()),
      UPDATE_REPORT_INTERVAL,
    );

    this._postReport(this._createContentStartedReport());
  }

  onPlaybackFinished() {
    this._createContentEndedReport();
    this._playerState = PLAYER_STATE.STOPPED;
    this.isInitialized = false;
    this._sequenceId = 0;
    this._startPlaybackTimeInMs = 0;
    this._sessionId = undefined;
    this._viewedTime = undefined;
    this._aggregateBitrates = [];

    clearInterval(this._updateReportIntervalId);
    clearInterval(this._updateBitrateIntervalId);

    this.logAnalytics(TAG, ' >>>>>  ', 'playback finished');
  }

  onCafEvent(event: Object) {
    this.logAnalytics(TAG, 'Player event: ', event.type, event);

    switch (event.type) {
      case 'PLAYER_LOADING': {
        this.checkQvtAndStart(() => {
          this._initialize();
        });
        break;
      }
      case 'REQUEST_PLAY': {
        this._playerState = PLAYER_STATE.PLAYING;
        if (this._viewedTime) this._viewedTime.start();
        break;
      }
      case 'REQUEST_PAUSE': {
        this._playerState = PLAYER_STATE.PAUSED;
        if (this._viewedTime) this._viewedTime.stop();
        break;
      }
      case 'ERROR': {
        this._createErrorRecord(event);
        event.handleInSuper = true;
        break;
      }
      default:
        event.handleInSuper = true;
        break;
    }
    return event;
  }

  onCafShutdown() {
    this.onPlaybackFinished();
  }

  _postReport(report: Object) {
    const data = `JSON1,${encrypt(report)}`;
    fetch(this.postUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'text/plain; charset=UTF-8',
      },
      body: data,
    })
      .then(response => this.logAnalytics(TAG, response))
      .catch(error => this.logAnalytics(TAG, error));
    this._sequenceId += 1;
    this._lastUpdateDate = new Date();
  }

  _createReportRecord() {
    return {
      timestamp: Math.floor(Date.now() / MS_IN_SECOND),
      sequenceId: this._sequenceId,
      '.tagId': 1,
    };
  }

  _createIdentityRecord() {
    return {
      uuid: this.viewerId,
      subscriberId: this.subscriberId,
      serviceId: this.serviceId,
      sessionId: this._sessionId,
      '.tagId': 2,
    };
  }

  _createHardwareRecord() {
    return {
      make: this.manufacturer,
      model: this.deviceName,
      '.tagId': 3,
    };
  }

  _createSoftwareRecord() {
    return {
      osName: this.osName,
      osFamily: this.osFamily,
      platformType: this.platformType,
      playerVersionMajor: getVersionMajor(this.playerVersion),
      playerVersionMinor: getVersionMinor(this.playerVersion),
      playerVersionRevision: getVersionRevision(this.playerVersion),
      playerVersionBuild: this.playerVersion,
      platformVendor: 'DishAnywhere',
      platformName: 'DANY-ONE',
      '.tagId': 4,
    };
  }

  _createContentStartedRecord() {
    return {
      playoutType: this.playoutType,
      entitlementId: this.channelId,
      entitlementName: this.franchiseName,
      entitlementCategory: this.entitlementCategory,
      assetGuid: this.assetGuid,
      assetPublisherId: this.publisherId,
      bestVideoBitrate: this.streamBandwidth,
      qvtUrl: this.qvtUrl,
      qmxUrl: this.qmxUrl,
      '.tagId': 5,
    };
  }

  _createContentUpdateRecord() {
    return {
      realTime: new Date() - this._lastUpdateDate,
      playoutType: this.playoutType,
      viewedTime: this._viewedTime?.value,
      entitlementId: this.channelId,
      entitlementName: this.franchiseName,
      entitlementCategory: this.entitlementCategory,
      assetGuid: this.assetGuid,
      assetPublisherId: this.publisherId,
      qvtUrl: this.qvtUrl,
      qmxUrl: this.qmxUrl,
      '.tagId': 6,
    };
  }

  _createContentEndedRecord() {
    return {
      realTime: new Date() - this._lastUpdateDate,
      playoutType: this.playoutType,
      entitlementId: this.channelId,
      entitlementName: this.franchiseName,
      entitlementCategory: this.entitlementCategory,
      viewedTime: this._viewedTime?.value,
      assetGuid: this.assetGuid,
      assetPublisherId: this.publisherId,
      qvtUrl: this.qvtUrl,
      qmxUrl: this.qmxUrl,
      '.tagId': 7,
    };
  }

  _createContentViewedRecord() {
    return {
      playoutType: this.playoutType,
      contentSource: this.contentSource,
      startPosMS: this._startPlaybackTimeInMs,
      endPosMS: this.playbackTimeInMs,
      '.tagId': 8,
    };
  }

  _createUserExperienceRecord() {
    return {
      bitrateMean: mean(this._aggregateBitrates),
      frameDropCnt: this.totalDroppedFrames,
      stallDurationTotalMs: this.bufferingTime,
      '.tagId': 13,
    };
  }

  _createProfileRecord() {
    return {
      viewedTime: this._viewedTime?.value,
      bestVideoBitrate: this.streamBandwidth,
      videoFrameRate: this.encodedFramerate,
      resolutionWidth: this.contentWidth,
      resolutionHeight: this.contentHeight,
      '.tagId': 19,
    };
  }

  _createContentRecord() {
    return {
      playoutType: this.playoutType,
      contentId: this.contentId,
      entitlementId: this.channelId,
      entitlementName: this.franchiseName,
      entitlementCategory: this.entitlementCategory,
      contentSource: this.contentSource,
      assetGuid: this.assetGuid,
      assetPublisherId: this.publisherId,
      qvtUrl: this.qvtUrl,
      qmxUrl: this.qmxUrl,
      bestVideoBitrate: this.streamBandwidth,
      '.tagId': 22,
    };
  }

  _createEventRecord() {
    return {
      contentId: this.contentId,
      playerState: this._playerState,
      timestamp: Math.floor(Date.now() / MS_IN_SECOND),
      '.tagId': 23,
    };
  }

  _createCnpRecord() {
    return {
      playoutType: this.playoutType,
      sourceIpAddress: this.externalPublicIp,
      sourceName: this.cdnName,
      '.tagId': 24,
    };
  }

  _createCnpPerformanceRecord() {
    return {
      bitrateMean: mean(this._aggregateBitrates) || 0,
      '.tagId': 25,
    };
  }

  _createErrorRecord(event: Object) {
    return {
      contentId: this.contentId,
      playerState: PLAYER_STATE.ERROR,
      errorMessage: event.error.reason,
      detailMessage: event.error.reason,
      severity: 3, // 1=FATAL, 2=NON-FATAL (deprecated), 3=ERROR, 4=WARN
      code: event.detailedErrorCode,
      '.tagId': 17,
    };
  }

  _createReport(items) {
    return {
      major: 5, // major version changes are not backwards-compatible
      minor: 0, // minor version changes are always backwards-compatible
      items, // list of records
    };
  }

  _createContentStartedReport() {
    return this._createReport([
      this._createReportRecord(),
      this._createIdentityRecord(),
      this._createHardwareRecord(),
      this._createSoftwareRecord(),
      this._createContentStartedRecord(),
      this._createContentRecord(),
      this._createCnpRecord(),
      this._createEventRecord(),
    ]);
  }

  _createContentUpdatedReport() {
    return this._createReport([
      this._createReportRecord(),
      this._createIdentityRecord(),
      this._createHardwareRecord(),
      this._createSoftwareRecord(),
      this._createContentRecord(),
      this._createCnpRecord(),
      this._createUserExperienceRecord(),
      this._createContentUpdateRecord(),
      this._createProfileRecord(),
      this._createCnpPerformanceRecord(),
      this._createContentViewedRecord(),
      this._createEventRecord(),
    ]);
  }

  _createContentEndedReport() {
    return this._createReport([
      this._createReportRecord(),
      this._createIdentityRecord(),
      this._createHardwareRecord(),
      this._createSoftwareRecord(),
      this._createContentRecord(),
      this._createCnpRecord(),
      this._createUserExperienceRecord(),
      this._createContentEndedRecord(),
      this._createProfileRecord(),
      this._createCnpPerformanceRecord(),
      this._createContentViewedRecord(),
      this._createEventRecord(),
    ]);
  }
}

export default StatsPostCastAnalytics;
